import { Badge, Button, Card, CardContent, Checkbox } from "@coval/ui"
import { AlertTriangle, CheckCircle } from "lucide-react"

import type { Annotation } from "@/types/api/Annotation"

import type {
  DisagreementAnalysis,
  FeedbackTheme,
} from "../optimization/optimization"

interface FeedbackPreviewPanelProps {
  annotations: Annotation[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  currentUserId?: string
  disagreementData: DisagreementAnalysis
  totalAnnotationCount?: number
  isActive?: boolean
}

export const FeedbackPreviewPanel = ({
  annotations,
  selectedIds,
  onSelectionChange,
  currentUserId,
  disagreementData,
  totalAnnotationCount,
  isActive = true,
}: FeedbackPreviewPanelProps) => {
  const {
    comparableUnits,
    comparisonsByAnnotationId,
    machineDisagreementRate,
    machineDisagreements,
    nonComparableAnnotations,
    themes,
  } = disagreementData

  const handleToggleAll = (select: boolean) => {
    if (select) {
      onSelectionChange(annotations.map((a) => a.id))
    } else {
      onSelectionChange([])
    }
  }

  const handleToggleAnnotation = (annotationId: string) => {
    if (selectedIds.includes(annotationId)) {
      onSelectionChange(selectedIds.filter((id) => id !== annotationId))
    } else {
      onSelectionChange([...selectedIds, annotationId])
    }
  }

  const handleToggleTheme = (theme: FeedbackTheme) => {
    const themeIds = theme.annotationIds
    const allSelected = themeIds.every((id) => selectedIds.includes(id))

    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !themeIds.includes(id)))
    } else {
      const newIds = new Set([...selectedIds, ...themeIds])
      onSelectionChange(Array.from(newIds))
    }
  }

  const isThemeSelected = (theme: FeedbackTheme) => {
    return theme.annotationIds.every((id) => selectedIds.includes(id))
  }

  const isThemePartiallySelected = (theme: FeedbackTheme) => {
    const selectedCount = theme.annotationIds.filter((id) =>
      selectedIds.includes(id),
    ).length
    return selectedCount > 0 && selectedCount < theme.annotationIds.length
  }

  const formatAnnotationValue = (annotation: Annotation): string => {
    return (
      annotation.ground_truth_string_value ??
      annotation.ground_truth_float_value?.toString() ??
      annotation.ground_truth_set_value?.join(", ") ??
      "N/A"
    )
  }

  if (annotations.length === 0) {
    return (
      <Card className="border-none shadow-none">
        <CardContent>
          <p className="text-coval-text-muted text-sm">
            No annotations available for this metric.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      className={`border-none shadow-none transition-opacity ${!isActive ? "opacity-50" : ""}`}
    >
      <CardContent className="gap-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-sm">Annotations to Include</h4>
            {isActive ? (
              <Badge size="status" variant="info">
                Active
              </Badge>
            ) : (
              <span className="text-xs text-coval-text-muted">
                Select &quot;Address feedback issues&quot; to use
              </span>
            )}
            {totalAnnotationCount &&
              totalAnnotationCount > annotations.length && (
                <span className="text-xs text-coval-text-muted">
                  (showing {annotations.length} most recent of{" "}
                  {totalAnnotationCount})
                </span>
              )}
          </div>
          <div className="flex items-center gap-2">
            <Badge size="status">
              {selectedIds.length} of {annotations.length} selected
            </Badge>
            <Button
              variant="ghost"
              onClick={() =>
                handleToggleAll(selectedIds.length < annotations.length)
              }
              aria-label={
                selectedIds.length === annotations.length
                  ? "Deselect all annotations"
                  : "Select all annotations"
              }
            >
              {selectedIds.length === annotations.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </div>
        </div>

        {machineDisagreementRate > 0.3 && (
          <div className="flex items-start gap-2 p-3 bg-coval-warning-surface border border-coval-warning-border rounded-lg">
            <AlertTriangle
              className="w-4 h-4 text-coval-warning-on-surface mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <div className="text-sm">
              <p className="font-medium text-coval-warning-on-surface">
                High AI disagreement detected (
                {Math.round(machineDisagreementRate * 100)}%)
              </p>
              <p className="text-coval-warning-on-surface mt-1">
                AI labels differ from human labels on {machineDisagreements} of{" "}
                {comparableUnits} comparable label units.
              </p>
            </div>
          </div>
        )}

        {comparableUnits > 0 && machineDisagreementRate <= 0.3 && (
          <div className="flex items-center gap-2 p-2 bg-coval-success-surface border border-coval-success-border rounded-lg">
            <CheckCircle
              className="w-4 h-4 text-coval-success-on-surface"
              aria-hidden="true"
            />
            <p className="text-sm text-coval-success-on-surface">
              Human-AI agreement:{" "}
              {Math.round((1 - machineDisagreementRate) * 100)}% across{" "}
              {comparableUnits} comparable label units
            </p>
          </div>
        )}

        {nonComparableAnnotations > 0 && (
          <div className="flex items-start gap-2 p-2 bg-surface-1 border border-coval-border rounded-lg">
            <AlertTriangle
              className="w-4 h-4 text-coval-text-muted mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-sm text-coval-text-muted">
              {nonComparableAnnotations} annotation
              {nonComparableAnnotations === 1 ? " has" : "s have"} no comparable
              AI output and {nonComparableAnnotations === 1 ? "is" : "are"}{" "}
              excluded.
            </p>
          </div>
        )}

        {themes.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-coval-text-muted font-medium uppercase">
              Feedback Themes
            </p>
            {themes.slice(0, 5).map((theme) => (
              <div
                key={theme.theme}
                className="flex items-center gap-2 p-2 bg-surface-1 rounded-lg"
              >
                <Checkbox
                  checked={
                    isThemeSelected(theme)
                      ? true
                      : isThemePartiallySelected(theme)
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={() => handleToggleTheme(theme)}
                  aria-label={`Select theme: ${theme.theme}`}
                />
                <span className="text-sm flex-1">{theme.theme}</span>
                <Badge size="status">{theme.count}</Badge>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-2 max-h-48 overflow-y-auto">
          {annotations.map((annotation) => {
            const isMyAnnotation =
              currentUserId && annotation.assignee === currentUserId
            const comparison = comparisonsByAnnotationId[annotation.id]
            const comparisonBadge =
              comparison?.status === "agreement"
                ? { label: "Matches AI", variant: "success" as const }
                : comparison?.status === "disagreement"
                  ? { label: "Differs from AI", variant: "danger" as const }
                  : { label: "No AI comparison", variant: "warning" as const }
            return (
              <div
                key={annotation.id}
                className={`flex items-start gap-2 p-2 rounded-lg ${
                  isMyAnnotation ? "bg-coval-info-surface" : "bg-surface-1"
                }`}
              >
                <Checkbox
                  checked={selectedIds.includes(annotation.id)}
                  onCheckedChange={() => handleToggleAnnotation(annotation.id)}
                  aria-label={`Select annotation: ${formatAnnotationValue(annotation)}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge
                      size="status"
                      variant={isMyAnnotation ? "info" : "default"}
                    >
                      {formatAnnotationValue(annotation)}
                    </Badge>
                    {isMyAnnotation && (
                      <span className="text-xs text-coval-info-on-surface">
                        You
                      </span>
                    )}
                    <Badge size="status" variant={comparisonBadge.variant}>
                      {comparisonBadge.label}
                    </Badge>
                  </div>
                  {annotation.annotations?.custom_explanation && (
                    <p className="text-xs text-coval-text-muted mt-1 line-clamp-2">
                      {annotation.annotations.custom_explanation}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
